/**
 * AppHeader - Top header bar ("Information Strip")
 *
 * 3-zone layout:
 * - Left:   Page title + environment badge (desk-aware slot)
 * - Center: Total P&L, position count, order count
 * - Right:  Game count, staleness timer, connection dots (K, F, P, R)
 */

import { useState, useRef, useEffect } from 'react';
import { FlaskConical, Volume2, VolumeX, AudioLines } from 'lucide-react';
import { cn } from '@/lib/infra/utils';
import { Money } from '@/components/atoms/Money';
import type { StalenessLevel } from '@/lib/infra/reconnect';
import { SoundSettingsPanel } from '@/components/organisms/SoundSettingsPanel';
import { readSoundSettings } from '@/hooks/ui/useSoundSettings';
import { playFillTone, playWhaleTone, playKaChingTone } from '@/lib/audio/tradeAudio';

/** Per-service connection status */
export interface ApiStatus {
  /** Kalshi REST API */
  kalshi: 'connected' | 'disconnected';
  /** WebSocket market data feed */
  feed: 'live' | 'offline';
  /** Polymarket CLOB client */
  poly: 'connected' | 'disconnected' | 'error';
  /** Relay server reachable */
  relay: boolean;
}

export interface AppHeaderProps {
  /** Page title or desk label */
  title?: string;
  /** Environment badge */
  environment?: 'prod' | 'demo';
  /** Total P&L across all positions */
  totalPnl?: number;
  /** Number of open positions */
  positionCount?: number;
  /** Number of open orders */
  orderCount?: number;
  /** Total number of games across all active streams */
  gameCount?: number;
  /** Feed staleness level */
  staleness?: StalenessLevel;
  /** Seconds since last feed update (null = never) */
  secondsSinceUpdate?: number | null;
  /** API connection statuses */
  apiStatus?: ApiStatus;
  /** Global test data toggle state */
  testData?: boolean;
  /** Callback to toggle test data */
  onToggleTestData?: () => void;
  /** Callback to replay last 3 fill sounds from Kalshi */
  onReplayFills?: () => Promise<void>;
}

type DotColor = 'green' | 'grey' | 'red' | 'yellow';

const DOT_BG: Record<DotColor, string> = {
  green: 'bg-emerald-500',
  yellow: 'bg-amber-400',
  grey: 'bg-zinc-600',
  red: 'bg-red-500',
};

function ConnectionDot({ label, color, title }: { label: string; color: DotColor; title: string }) {
  return (
    <div
      className="flex items-center gap-1"
      title={title}
    >
      <span className={cn('h-2 w-2 rounded-full', DOT_BG[color])} />
      <span className="text-muted-foreground text-[10px] font-medium uppercase">{label}</span>
    </div>
  );
}

function stalenessColor(level: StalenessLevel): string {
  switch (level) {
    case 'fresh':
      return 'text-emerald-400';
    case 'stale':
      return 'text-amber-400';
    case 'disconnected':
      return 'text-red-400';
  }
}

function formatStaleness(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '--:--';
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function AppHeader({
  title,
  environment,
  totalPnl,
  positionCount,
  orderCount,
  gameCount,
  staleness = 'disconnected',
  secondsSinceUpdate,
  apiStatus,
  testData,
  onToggleTestData,
  onReplayFills,
}: AppHeaderProps) {
  const [soundOpen, setSoundOpen] = useState(false);
  const [testingSound, setTestingSound] = useState(false);
  const soundRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!soundOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (soundRef.current && !soundRef.current.contains(e.target as Node)) {
        setSoundOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [soundOpen]);

  const soundSettings = readSoundSettings();
  const allMuted = !soundSettings.tradeSoundEnabled && !soundSettings.defenseSoundEnabled;

  return (
    <div className="flex h-full items-center justify-between px-4">
      {/* Left: page title + environment badge */}
      <div className="flex items-center gap-2">
        {title && <h1 className="text-sm font-medium">{title}</h1>}
        {environment === 'demo' && (
          <span className="rounded bg-amber-500/20 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-400">
            demo
          </span>
        )}
      </div>

      {/* Center: P&L + position/order counts */}
      <div className="flex items-center gap-4">
        {totalPnl !== undefined && (
          <div
            className="flex items-center gap-1.5"
            title="Total P&L across all positions"
          >
            <span className="text-muted-foreground text-xs">P&L</span>
            <Money
              value={totalPnl}
              variant="pnl"
              className="font-mono text-sm font-medium"
            />
          </div>
        )}
        {positionCount !== undefined && (
          <div
            className="flex items-center gap-1"
            title={`${positionCount} open position${positionCount !== 1 ? 's' : ''}`}
          >
            <span className="text-muted-foreground text-xs">Pos</span>
            <span className="font-mono text-sm">{positionCount.toLocaleString()}</span>
          </div>
        )}
        {orderCount !== undefined && (
          <div
            className="flex items-center gap-1"
            title={`${orderCount} open order${orderCount !== 1 ? 's' : ''}`}
          >
            <span className="text-muted-foreground text-xs">Ord</span>
            <span className="font-mono text-sm">{orderCount.toLocaleString()}</span>
          </div>
        )}
      </div>

      {/* Right: game count + staleness timer + connection dots */}
      <div className="flex items-center gap-3">
        {/* Game count */}
        {gameCount !== undefined && (
          <div
            className="flex items-center gap-1"
            title={`${gameCount} game${gameCount !== 1 ? 's' : ''} tracked`}
          >
            <span className="text-muted-foreground text-xs">Games</span>
            <span className="font-mono text-sm">{gameCount.toLocaleString()}</span>
          </div>
        )}

        {/* Test Data toggle */}
        {onToggleTestData && (
          <button
            onClick={onToggleTestData}
            className={cn(
              'flex items-center gap-1 rounded px-1.5 py-0.5 text-xs transition-colors',
              testData
                ? 'bg-amber-500/20 text-amber-400'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            )}
            title={testData ? 'Test data active — click to disable' : 'Enable test data'}
          >
            <FlaskConical className="h-3 w-3" />
            <span>Test</span>
          </button>
        )}

        {/* Test Sound — quick fill→fill→whale sequence */}
        {!allMuted && (
          <button
            onClick={async () => {
              setTestingSound(true);
              playKaChingTone();
              await new Promise((r) => setTimeout(r, 800));
              playFillTone();
              await new Promise((r) => setTimeout(r, 800));
              playWhaleTone();
              await new Promise((r) => setTimeout(r, 600));
              setTestingSound(false);
            }}
            disabled={testingSound}
            className={cn(
              'flex items-center gap-1 rounded px-1.5 py-0.5 text-xs transition-colors',
              testingSound
                ? 'bg-emerald-500/20 text-emerald-400'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            )}
            title="Test sound — plays ka-ching, fill, whale"
          >
            <AudioLines className="h-3 w-3" />
            <span>{testingSound ? 'Playing…' : 'Test Sound'}</span>
          </button>
        )}

        {/* Sound controls popover */}
        <div
          ref={soundRef}
          className="relative"
        >
          <button
            onClick={() => setSoundOpen((v) => !v)}
            className={cn(
              'flex items-center gap-1 rounded px-1.5 py-0.5 text-xs transition-colors',
              soundOpen
                ? 'bg-muted text-foreground'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            )}
            title="Sound settings"
          >
            {allMuted ? <VolumeX className="h-3 w-3" /> : <Volume2 className="h-3 w-3" />}
            <span>Sound</span>
          </button>
          {soundOpen && (
            <div className="bg-card border-border absolute right-0 top-full z-50 mt-1 w-72 rounded-lg border p-3 shadow-xl">
              <SoundSettingsPanel
                compact
                onReplayFills={onReplayFills}
              />
            </div>
          )}
        </div>

        {/* Staleness timer */}
        <div
          className={cn('flex items-center gap-1', stalenessColor(staleness))}
          title={
            staleness === 'fresh'
              ? 'Last update is fresh'
              : staleness === 'stale'
                ? 'Last update is stale (>15s ago)'
                : 'Disconnected (>60s since last update)'
          }
        >
          <span className="text-muted-foreground text-xs">Last Update</span>
          <span className="font-mono text-xs">{formatStaleness(secondsSinceUpdate)}</span>
        </div>

        {/* Connection dots */}
        {apiStatus && (
          <div className="flex items-center gap-2">
            <ConnectionDot
              label="K"
              color={apiStatus.kalshi === 'connected' ? 'green' : 'grey'}
              title={
                apiStatus.kalshi === 'connected'
                  ? 'Kalshi API connected'
                  : 'Kalshi API disconnected'
              }
            />
            <ConnectionDot
              label="F"
              color={
                staleness === 'fresh'
                  ? 'green'
                  : staleness === 'stale'
                    ? 'yellow'
                    : apiStatus.feed === 'live'
                      ? 'yellow'
                      : 'grey'
              }
              title={
                staleness === 'fresh'
                  ? 'Feed data streaming (fresh)'
                  : staleness === 'stale'
                    ? 'Feed data stale'
                    : apiStatus.feed === 'live'
                      ? 'Feed connected but no recent data'
                      : 'Feed offline'
              }
            />
            <ConnectionDot
              label="P"
              color={
                apiStatus.poly === 'connected'
                  ? 'green'
                  : apiStatus.poly === 'error'
                    ? 'red'
                    : 'grey'
              }
              title={
                apiStatus.poly === 'connected'
                  ? 'Polymarket connected'
                  : apiStatus.poly === 'error'
                    ? 'Polymarket error'
                    : 'Polymarket not connected'
              }
            />
            <ConnectionDot
              label="R"
              color={apiStatus.relay ? 'green' : 'grey'}
              title={apiStatus.relay ? 'Relay server reachable' : 'Relay server unreachable'}
            />
          </div>
        )}
      </div>
    </div>
  );
}
